import React, { useState, useCallback } from "react";
import { useRef } from "react";
import Cropper from "react-easy-crop";
import Modal from "react-bootstrap/Modal";
import getCroppedImg from "./cropImage.js";
import useFileUpload from "../../../../hooks/dataSenders/singleUpload.js";
import MultipartUploadService from "../../../../services/MultipartUploadService.js";
import { Api_Url } from "../../../../utils/ApiUrl.js";
import axios from "axios";

const Media = ({
  projectSetup,
  setProjectSetup,
  setLoader,
  youtubeErrors,
  setYoutubeErrors,
  projectId,
}) => {
  const [show, setShow] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropAspect, setCropAspect] = useState(1); // default square
  const [currentField, setCurrentField] = useState(null);
  const removeDocumentField = (index) => {
    setProjectSetup((prev) => ({
      ...prev,
      documents: {
        ...prev.documents,
        value: prev.documents.value.filter((_, i) => i !== index),
      },
    }));
  };

  const desktopInputRef = useRef(null);
  const mobileInputRef = useRef(null);
  const darkChartInputRef = useRef(null);
  const lightChartInputRef = useRef(null);

  // integration states
  const { uploadFile } = useFileUpload();
  const [key, setKey] = useState(null);

  // Initialize the service
  const uploadService = new MultipartUploadService({
    baseURL: Api_Url,
    chunkSize: 5 * 1024 * 1024, // 5MB chunks
    threadsQuantity: 5, // 5 parallel uploads
    getAuthToken: () => localStorage.getItem("accessToken"),
    transformUrl: (url) => {
      // Transform S3 URL to CloudFront
      if (url.startsWith("https://gems-media.s3.us-west-2.amazonaws.com/")) {
        return url.replace(
          "https://gems-media.s3.us-west-2.amazonaws.com/",
          "https://dh3bzuu7a45gi.cloudfront.net/"
        );
      }
      return url;
    },
  });

  // --- Project Media Upload ---
  const handleClickUpload = () => {
    document.getElementById("mediaInput").click();
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Calculate total size of all selected files
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    const maxSize = 300 * 1024 * 1024; // 300MB in bytes

    // Validate total file size
    if (totalSize > maxSize) {
      setProjectSetup((prev) => ({
        ...prev,
        projectMedia: {
          ...prev.projectMedia,
          error: true,
          errorMsg: `Total file size exceeds 300MB. Selected: ${(
            totalSize /
            1024 /
            1024
          ).toFixed(2)}MB`,
        },
      }));
      e.target.value = "";
      return;
    }

    // Clear any previous errors
    setProjectSetup((prev) => ({
      ...prev,
      projectMedia: {
        ...prev.projectMedia,
        error: false,
        errorMsg: "",
      },
    }));

    const getTitle = (filename) => {
      return filename;
    };

    // Create unique IDs for each file
    const newMediaItems = files.map((file) => ({
      id: `${Date.now()}-${Math.random()}`, // Unique ID for tracking
      title: getTitle(file.name),
      file,
      progress: 0,
      url: null,
    }));

    // 1️⃣ Add all files immediately with 0% progress
    setProjectSetup((prev) => ({
      ...prev,
      projectMedia: {
        ...prev.projectMedia,
        value: [...prev.projectMedia.value, ...newMediaItems],
        error: false,
        errorMsg: "",
      },
    }));

    // 2️⃣ Upload all files in parallel
    for (const mediaItem of newMediaItems) {
      let fakeProgressInterval = null;
      let isUploadComplete = false;

      try {
        const fileSizeInMB = mediaItem.file.size / 1024 / 1024;
        const isSmallFile = fileSizeInMB < 5; // Files smaller than 5MB

        // Start fake progress for small files
        if (isSmallFile) {
          let fakeProgress = 0;
          fakeProgressInterval = setInterval(() => {
            if (isUploadComplete) {
              clearInterval(fakeProgressInterval);
              return;
            }

            fakeProgress += Math.random() * 10 + 5; // Random increment between 5-15%
            if (fakeProgress > 85) fakeProgress = 85; // Cap at 85% until real upload completes

            setProjectSetup((prev) => ({
              ...prev,
              projectMedia: {
                ...prev.projectMedia,
                value: prev.projectMedia.value.map((item) =>
                  item.id === mediaItem.id
                    ? { ...item, progress: Math.min(fakeProgress, 85) }
                    : item
                ),
              },
            }));
          }, 300); // Update every 300ms (slower for smoother animation)
        }

        const result = await uploadService.uploadFile(mediaItem.file, {
          destination: "project",

          onProgress: (percentage) => {
            if (isUploadComplete) return;

            // For small files, blend fake and real progress
            if (isSmallFile) {
              setProjectSetup((prev) => ({
                ...prev,
                projectMedia: {
                  ...prev.projectMedia,
                  value: prev.projectMedia.value.map((item) => {
                    if (item.id === mediaItem.id) {
                      const currentProgress = item.progress || 0;
                      // Only update if real progress is higher
                      const newProgress = Math.max(currentProgress, percentage);
                      return {
                        ...item,
                        progress: Math.min(newProgress, 95), // Cap at 95 until complete
                      };
                    }
                    return item;
                  }),
                },
              }));
            } else {
              // For large files, use real progress
              setProjectSetup((prev) => ({
                ...prev,
                projectMedia: {
                  ...prev.projectMedia,
                  value: prev.projectMedia.value.map((item) =>
                    item.id === mediaItem.id
                      ? { ...item, progress: percentage }
                      : item
                  ),
                },
              }));
            }
          },

          onPartComplete: (partNumber, totalParts) => {
            console.log(
              `Uploaded part ${partNumber}/${totalParts} for file: ${mediaItem.title}`
            );
          },
        });

        // Mark upload as complete
        isUploadComplete = true;

        // Clear fake progress interval
        if (fakeProgressInterval) {
          clearInterval(fakeProgressInterval);
        }

        // Small delay before showing 100% to ensure smooth transition
        if (isSmallFile) {
          await new Promise((resolve) => setTimeout(resolve, 300));
        }

        // 3️⃣ Mark upload complete + save URL using unique ID
        setProjectSetup((prev) => ({
          ...prev,
          projectMedia: {
            ...prev.projectMedia,
            value: prev.projectMedia.value.map((item) =>
              item.id === mediaItem.id
                ? {
                    ...item,
                    progress: 100,
                    url: result.fileUrl,
                  }
                : item
            ),
            error: false,
            errorMsg: "",
          },
        }));
      } catch (error) {
        console.error(`Upload failed for ${mediaItem.title}:`, error);

        // Clear fake progress interval on error
        if (fakeProgressInterval) {
          clearInterval(fakeProgressInterval);
        }

        // Remove failed upload using unique ID
        setProjectSetup((prev) => ({
          ...prev,
          projectMedia: {
            ...prev.projectMedia,
            value: prev.projectMedia.value.filter(
              (item) => item.id !== mediaItem.id
            ),
          },
        }));

        alert(`Upload failed for ${mediaItem.title}: ${error.message}`);
        // continues to next file even if this one fails
      }
    }

    e.target.value = ""; // Reset input after all uploads
  };

  const handleDelete = (index, item) => {
    let data = JSON.stringify({
      url: [item?.url],
    });
    const token = localStorage.getItem("accessToken");

    let config = {
      method: "delete",
      maxBodyLength: Infinity,
      url: `${Api_Url}/uploads/`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ` + token,
      },
      data: data,
    };

    axios
      .request(config)
      .then((response) => {
        // console.log(JSON.stringify(response.data));
      })
      .catch((error) => {
        console.log(error);
      });

    setProjectSetup((prev) => ({
      ...prev,
      projectMedia: {
        ...prev.projectMedia,
        value: prev.projectMedia.value.filter((_, i) => i !== index),
        error: false,
        errorMsg: "",
      },
    }));
  };

  const addDocumentField = () => {
    setProjectSetup((prev) => ({
      ...prev,
      documents: {
        ...prev.documents,
        value: [...prev.documents.value, { name: "", file: null }],
        error: false,
        errorMsg: "",
      },
    }));
  };

  const handleDocNameChange = (i, value) => {
    setProjectSetup((prev) => ({
      ...prev,
      documents: {
        ...prev.documents,
        value: prev.documents.value.map((doc, index) =>
          index === i ? { ...doc, name: value } : doc
        ),
        error: false,
        errorMsg: "",
      },
    }));
  };

  const uploadDoc = (i) => {
    document.getElementById(`docInput-${i}`).click();
  };

  const handleDocUpload = async (i, e, id) => {
    const file = e.target.files[0];

    if (!file) return;

    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      setProjectSetup((prev) => ({
        ...prev,
        documents: {
          ...prev.documents,
          error: true,
          errorMsg: "File size must be less than 10MB",
        },
      }));
      e.target.value = "";
      return;
    }

    if (file?.type !== "application/pdf") {
      setProjectSetup((prev) => ({
        ...prev,
        documents: {
          ...prev.documents,
          error: true,
          errorMsg: "Please upload PDF files only",
        },
      }));
      return;
    }

    try {
      // Upload the file and get URL
      const url = await uploadFile(file, setLoader);

      if (url) {
        setProjectSetup((prev) => ({
          ...prev,
          documents: {
            ...prev.documents,
            value: prev.documents.value.map((doc, index) =>
              index === i ? { ...doc, file: url, file2: file } : doc
            ),
            error: false,
            errorMsg: "",
          },
        }));
      }
    } catch (error) {
      console.error("Upload failed:", error);
      setProjectSetup((prev) => ({
        ...prev,
        documents: {
          ...prev.documents,
          error: true,
          errorMsg: "Failed to upload document. Please try again.",
        },
      }));
    }
    const fileInput = document.getElementById(id);
    if (fileInput) fileInput.value = "";
  };

  // --- Image Cropper Logic ---
  const handleShow = () => setShow(true);
  const handleClose = () => setShow(false);

  const onFileCrop = (e, fieldKey, aspectRatio) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = () => {
      setCurrentField(fieldKey); // which state to update later
      setCropAspect(aspectRatio); // set aspect ratio for this image
      setImageSrc(reader.result);
      setShow(true);
    };
  };

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const base64ToFile = (base64String, filename = "image.png") => {
    // Remove the data:image/png;base64, part
    const arr = base64String.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };

  const handleSave = async () => {
    try {
      const cropped = await getCroppedImg(imageSrc, croppedAreaPixels);
      const croppedFile = base64ToFile(cropped, "cropped-image.png");

      setZoom(1);
      setShow(false);

      const url = await uploadFile(croppedFile, setLoader);

      setProjectSetup((prev) => ({
        ...prev,
        [currentField]: {
          ...prev[currentField],
          value: url,
          error: false,
          errorMsg: "",
        },
      }));

      // reset
      setImageSrc(null);
      setCurrentField(null);
    } catch (err) {
      console.error(err);
    }
  };

  const addYoutubeLink = () => {
    setProjectSetup((prev) => ({
      ...prev,
      youtubeEmbeds: {
        ...prev.youtubeEmbeds,
        value: [...prev.youtubeEmbeds.value, ""],
      },
    }));

    // Add error tracking for new link
    setYoutubeErrors((prev) => ({
      errors: [...prev.errors, false],
      errorMsgs: [...prev.errorMsgs, ""],
    }));
  };

  const handleYoutubeChange = (index, value) => {
    // Update value in original state
    const updatedLinks = [...projectSetup.youtubeEmbeds.value];
    updatedLinks[index] = value;

    setProjectSetup((prev) => ({
      ...prev,
      youtubeEmbeds: {
        ...prev.youtubeEmbeds,
        value: updatedLinks,
      },
    }));

    setYoutubeErrors({
      errors: [false],
      errorMsgs: [""],
    });
  };

  const removeYoutubeLink = (index) => {
    setProjectSetup((prev) => ({
      ...prev,
      youtubeEmbeds: {
        ...prev.youtubeEmbeds,
        value: prev.youtubeEmbeds.value.filter((_, i) => i !== index),
      },
    }));

    // Remove error tracking for this link
    setYoutubeErrors((prev) => ({
      errors: prev.errors.filter((_, i) => i !== index),
      errorMsgs: prev.errorMsgs.filter((_, i) => i !== index),
    }));
  };

  const removeProfileImage = async () => {
    let data = JSON.stringify({
      url: [projectSetup?.projectImage?.value],
    });
    const token = localStorage.getItem("accessToken");

    let config = {
      method: "delete",
      maxBodyLength: Infinity,
      url: `${Api_Url}/uploads/`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ` + token,
      },
      data: data,
    };

    axios
      .request(config)
      .then((response) => {
        // console.log(JSON.stringify(response.data));
      })
      .catch((error) => {
        console.log(error);
      });
    setProjectSetup((prev) => ({
      ...prev,
      projectImage: {
        ...prev.projectImage,
        value: null,
        error: false,
        errorMsg: "",
      },
    }));
    const fileInput = document.getElementById("imgUploadInput");
    if (fileInput) fileInput.value = "";
  };
  const handleFileChange = (e, field, ratio) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 3 * 1024 * 1024;

    if (file.size > maxSize) {
      setProjectSetup((prev) => ({
        ...prev,
        [field]: {
          ...prev[field],
          error: true,
          errorMsg: "File size must be less than 3MB",
        },
      }));
      e.target.value = "";
      return;
    }

    setProjectSetup((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        error: false,
        errorMsg: "",
      },
    }));

    onFileCrop(e, field, ratio);
    e.target.value = "";
  };
  const darkInputRef = useRef(null);
  const lightInputRef = useRef(null);

  console.log("projectSetup?.documents", projectSetup?.documents);

  return (
    <>
      <div className="parentsteps">
        <section className="inner_stepssecond">
          {/* --- Project Image Upload --- */}
          <div
            className="second_stepdata"
            style={{ display: "flex", alignItems: "center", gap: "12px" }}
          >
            <div
              className="divimg"
              onClick={() => document.getElementById("imgUploadInput").click()}
              style={{
                cursor: "pointer",
                width: "120px",
                height: "120px",
                borderRadius: "360px",
                overflow: "hidden",
              }}
            >
              {projectSetup?.projectImage?.value ? (
                <img
                  src={projectSetup?.projectImage?.value}
                  alt="Uploaded"
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "360px",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <img src="/assets/downlodnew.svg" alt="Placeholder" />
              )}
            </div>

            <div className="rightside_img">
              <h4
                onClick={() =>
                  document.getElementById("imgUploadInput").click()
                }
              >
                Project Image<span style={{ color: "red" }}>*</span>
              </h4>
              <p>800×800 resolution • Max 3MB • JPEG/ PNG</p>
              <div className="buttons_twoo">
                <button
                  onClick={() =>
                    document.getElementById("imgUploadInput").click()
                  }
                >
                  {projectSetup?.projectImage?.value
                    ? "Replace"
                    : "Upload Image"}
                </button>
                {projectSetup?.projectImage?.value && (
                  <button
                    className="removebuttonred"
                    onClick={() => removeProfileImage()}
                  >
                    Remove
                  </button>
                )}
              </div>

              <input
                type="file"
                id="imgUploadInput"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const maxSize = 3 * 1024 * 1024;
                    if (file.size > maxSize) {
                      setProjectSetup((prev) => ({
                        ...prev,
                        projectImage: {
                          ...prev.projectImage,
                          error: true,
                          errorMsg: "File size must be less than 3MB",
                        },
                      }));
                      e.target.value = "";
                      return;
                    }
                    setProjectSetup((prev) => ({
                      ...prev,
                      projectImage: {
                        ...prev.projectImage,
                        error: false,
                        errorMsg: "",
                      },
                    }));
                    onFileCrop(e, "projectImage", 1);
                    e.target.value = "";
                  }
                }}
              />
              {projectSetup?.projectImage?.error && (
                <p className="error-text">
                  {projectSetup?.projectImage?.errorMsg}
                </p>
              )}
            </div>
          </div>

          {/* --- Desktop & Mobile Banner --- */}
          <div className="top_twosides">
            {/* Desktop */}
            <div className="leftside_tab">
              {projectSetup?.projectBannerDesktop?.value && (
                <div
                  className="bothbutton"
                  style={{ display: "flex", gap: "8px" }}
                >
                  <button
                    type="button"
                    className="replacebutton"
                    onClick={() => desktopInputRef.current?.click()}
                  >
                    Replace
                  </button>
                  <button
                    type="button"
                    className="removebutton"
                    onClick={() =>
                      setProjectSetup((prev) => ({
                        ...prev,
                        projectBannerDesktop: {
                          ...prev.projectBannerDesktop,
                          value: null,
                          error: false,
                          errorMsg: "",
                        },
                      }))
                    }
                  >
                    Remove
                  </button>
                </div>
              )}

              <h5>
                Project Banner (Desktop)<span>*</span>
              </h5>

              <label
                onClick={() => desktopInputRef.current?.click()}
                className={
                  projectSetup?.projectBannerDesktop?.error
                    ? "lowerbox error"
                    : "lowerbox"
                }
                style={{ cursor: "pointer" }}
              >
                {projectSetup?.projectBannerDesktop?.value ? (
                  <img
                    src={projectSetup.projectBannerDesktop.value}
                    alt="Banner"
                    className="imgbanner"
                  />
                ) : (
                  <>
                    <img src="/assets/downlodimg.svg" alt="Placeholder" />
                    <h6>Upload Project Banner (Desktop)</h6>
                    <h3 style={{ fontSize: "12px", opacity: 0.7 }}>
                      2880x660 resolution. Max 3 MB in JPEG or PNG format
                    </h3>
                  </>
                )}
              </label>

              <input
                type="file"
                ref={desktopInputRef}
                hidden
                accept="image/png, image/jpeg"
                onChange={(e) =>
                  handleFileChange(
                    e,
                    "projectBannerDesktop",
                    window.innerWidth / 330
                  )
                }
              />

              {projectSetup?.projectBannerDesktop?.error && (
                <p className="error_text">
                  {projectSetup.projectBannerDesktop.errorMsg}
                </p>
              )}
            </div>

            {/* Mobile */}
            <div className="leftside_tab">
              {projectSetup?.projectBannerMobile?.value && (
                <div
                  className="bothbutton"
                  style={{ display: "flex", gap: "8px" }}
                >
                  <button
                    type="button"
                    className="replacebutton"
                    onClick={() => mobileInputRef.current?.click()}
                  >
                    Replace
                  </button>
                  <button
                    type="button"
                    className="removebutton"
                    onClick={() =>
                      setProjectSetup((prev) => ({
                        ...prev,
                        projectBannerMobile: {
                          ...prev.projectBannerMobile,
                          value: null,
                          error: false,
                          errorMsg: "",
                        },
                      }))
                    }
                  >
                    Remove
                  </button>
                </div>
              )}

              <h5>
                Project Banner (Mobile)<span>*</span>
              </h5>

              <label
                onClick={() => mobileInputRef.current?.click()}
                className={
                  projectSetup?.projectBannerMobile?.error
                    ? "lowerbox error"
                    : "lowerbox"
                }
                style={{ cursor: "pointer" }}
              >
                {projectSetup?.projectBannerMobile?.value ? (
                  <img
                    src={projectSetup.projectBannerMobile.value}
                    alt="Banner"
                    className="imgbanner"
                  />
                ) : (
                  <>
                    <img src="/assets/downlodimg.svg" alt="Placeholder" />
                    <h6>Upload Project Banner (Mobile)</h6>
                    <h3 style={{ fontSize: "12px", opacity: 0.7 }}>
                      880x658 resolution. Max 3 MB in JPEG or PNG format{" "}
                    </h3>
                  </>
                )}
              </label>

              <input
                type="file"
                ref={mobileInputRef}
                hidden
                accept="image/png, image/jpeg"
                onChange={(e) =>
                  handleFileChange(
                    e,
                    "projectBannerMobile",
                    window.innerWidth / 330
                  )
                }
              />

              {projectSetup?.projectBannerMobile?.error && (
                <p className="error_text">
                  {projectSetup.projectBannerMobile.errorMsg}
                </p>
              )}
            </div>
          </div>

          <div className="top_twosides">
            <div className="leftside_tab">
              {projectSetup?.tokenomicsChartDark?.value && (
                <div
                  className="bothbutton"
                  style={{ display: "flex", gap: "8px" }}
                >
                  <button
                    type="button"
                    className="replacebutton"
                    onClick={() => darkInputRef.current?.click()}
                  >
                    Replace
                  </button>
                  <button
                    type="button"
                    className="removebutton"
                    onClick={() =>
                      setProjectSetup((prev) => ({
                        ...prev,
                        tokenomicsChartDark: {
                          ...prev.tokenomicsChartDark,
                          value: null,
                          error: false,
                          errorMsg: "",
                        },
                      }))
                    }
                  >
                    Remove
                  </button>
                </div>
              )}

              <h5>Tokenomics Chart (Dark Theme)</h5>

              <label
                onClick={() => darkInputRef.current?.click()}
                className="lowerbox"
              >
                {projectSetup?.tokenomicsChartDark?.value ? (
                  <img
                    src={projectSetup.tokenomicsChartDark.value}
                    alt="Chart"
                    className="imgbanner"
                  />
                ) : (
                  <>
                    <img src="/assets/downlodimg.svg" alt="Placeholder" />
                    <h6>Tokenomics Chart (Dark Theme)</h6>
                    <h3 style={{ fontSize: "12px", opacity: 0.7 }}>
                      1848x1050 resolution. Max 3 MB in JPEG or PNG format
                    </h3>
                  </>
                )}
              </label>

              <input
                type="file"
                ref={darkInputRef}
                hidden
                accept="image/png, image/jpeg"
                onChange={(e) =>
                  handleFileChange(e, "tokenomicsChartDark", 16 / 9)
                }
              />
            </div>

            {/* Light */}
            <div className="leftside_tab">
              {projectSetup?.tokenomicsChartLight?.value && (
                <div
                  className="bothbutton"
                  style={{ display: "flex", gap: "8px" }}
                >
                  <button
                    type="button"
                    className="replacebutton"
                    onClick={() => lightInputRef.current?.click()}
                  >
                    Replace
                  </button>
                  <button
                    type="button"
                    className="removebutton"
                    onClick={() =>
                      setProjectSetup((prev) => ({
                        ...prev,
                        tokenomicsChartLight: {
                          ...prev.tokenomicsChartLight,
                          value: null,
                          error: false,
                          errorMsg: "",
                        },
                      }))
                    }
                  >
                    Remove
                  </button>
                </div>
              )}

              <h5>Tokenomics Chart (Light Theme)</h5>

              <label
                onClick={() => lightInputRef.current?.click()}
                className="lowerbox"
              >
                {projectSetup?.tokenomicsChartLight?.value ? (
                  <img
                    src={projectSetup.tokenomicsChartLight.value}
                    alt="Chart"
                    className="imgbanner"
                  />
                ) : (
                  <>
                    <img src="/assets/downlodimg.svg" alt="Placeholder" />
                    <h6>Tokenomics Chart (Light Theme)</h6>
                    <h3 style={{ fontSize: "12px", opacity: 0.7 }}>
                      1848x1050 resolution. Max 3 MB in JPEG or PNG format
                    </h3>
                  </>
                )}
              </label>

              <input
                type="file"
                ref={lightInputRef}
                hidden
                accept="image/png, image/jpeg"
                onChange={(e) =>
                  handleFileChange(e, "tokenomicsChartLight", 16 / 9)
                }
              />
            </div>
          </div>

          <div className="top_twosidess">
            <div className="leftside_tab lastnews">
              <h5 className="projjject_media">Project Media</h5>

              <div
                className={
                  projectSetup?.projectMedia?.error
                    ? "lowerbox error"
                    : "lowerbox"
                }
                style={{ cursor: "pointer", textAlign: "center" }}
                onClick={handleClickUpload}
              >
                <img src="/assets/downlodimg.svg" alt="Placeholder" />
                <h6>Drag & Drop Video or Image</h6>
                <h3 className="" style={{ fontSize: "12px", opacity: 0.7 }}>
                  Max File Size: 300 MB, File Format: PNG, JPG,<br></br> JPEG,
                  MP4
                </h3>
              </div>
              <input
                type="file"
                id="mediaInput"
                accept="image/png, image/jpeg, video/mp4"
                onChange={handleFileSelect}
                multiple
                hidden
              />
              {projectSetup.projectMedia.value.map((item, i) => (
                <div key={i} style={{ marginTop: "12px" }}>
                  <div className="progresssbarr newprogress">
                    <div className="fileinfo">
                      <h2>{item?.title}</h2>
                      {item?.file ? (
                        <p>
                          .{item.file.type.split("/")[1].toUpperCase()} |{" "}
                          {(item.file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      ) : (
                        <p style={{ color: "#e3ecf0" }}>.</p>
                      )}
                    </div>

                    <div className="innnnerprogress">
                      {item.progress === 100 || !item?.file ? (
                        <button
                          className="buttondeeel"
                          onClick={() => handleDelete(i, item)}
                        >
                          Delete
                        </button>
                      ) : (
                        <>
                          <div className="progresdiv">
                            <div
                              className="progressfill"
                              style={{ width: `${item.progress}%` }}
                            />
                          </div>
                          <span className="percentage">
                            {item.progress.toFixed(2)}%
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {projectSetup?.projectMedia?.error && (
                <p className="error_text" style={{ marginTop: 4 }}>
                  {projectSetup?.projectMedia?.errorMsg}
                </p>
              )}
            </div>
          </div>
          <div className="parent_links">
            <div
              // className={
              //   youtubeErrors?.errors[0] === true
              //     ? "links_lower error"
              //     : "links_lower"
              // }
              className="links_lower"
            >
              <h3>Youtube Embed</h3>

              {projectSetup?.youtubeEmbeds?.value.map((link, i) => (
                <div
                  className="linksunder"
                  key={i}
                  style={{
                    marginTop: "10px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    border: youtubeErrors.errors[i] ? "1px solid red" : "none",
                  }}
                >
                  <>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        width: "100%",
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                      >
                        <path
                          d="M12 7.5V6C12 4.34314 10.6568 3 9 3C7.34315 3 6 4.34314 6 6V7.5"
                          stroke="#329879"
                          strokeWidth="1.5"
                        />
                        <path
                          d="M12 10.5V12C12 13.6568 10.6568 15 9 15C7.34315 15 6 13.6568 6 12V10.5"
                          stroke="#329879"
                          strokeWidth="1.5"
                        />
                        <path d="M9 6V12" stroke="#329879" strokeWidth="1.5" />
                      </svg>

                      <input
                        type="text"
                        placeholder="https://www.youtube.com/"
                        value={link}
                        onChange={(e) => handleYoutubeChange(i, e.target.value)}
                        style={{
                          background: "transparent",
                          border: "none",
                          outline: "none",
                          width: "100%",
                        }}
                      />
                    </div>

                    {i !== 0 && (
                      <span
                        onClick={() => removeYoutubeLink(i)}
                        style={{
                          cursor: "pointer",
                          color: "red",
                          fontWeight: "500",
                        }}
                      >
                        Delete
                      </span>
                    )}
                  </>
                </div>
              ))}

              <button
                onClick={addYoutubeLink}
                style={{
                  marginTop: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                Add another{" "}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 10H15"
                    stroke="#02171D"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M10 15V5"
                    stroke="#02171D"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
            {youtubeErrors?.errors[0] === true && (
              <p className="error_text mt-3">{youtubeErrors?.errorMsgs[0]}</p>
            )}

            <div
              className={
                projectSetup?.documents?.error
                  ? "links_lower error"
                  : "links_lower"
              }
            >
              <div className="doctypeee">
                <h3>
                  Documents<span>*</span>
                </h3>
                <p>Format: PDF, Max Size: 10mb</p>
              </div>

              {projectSetup?.documents?.value.map((doc, i) => (
                <div
                  key={i}
                  className="left_links"
                  style={{ marginTop: "12px", position: "relative" }}
                >
                  <input
                    type="text"
                    placeholder="Document Name"
                    value={doc?.name}
                    onChange={(e) => handleDocNameChange(i, e.target.value)}
                  />

                  <div className="docsdoc">
                    <>
                      {projectId ? (
                        <h4>{doc?.name ? doc?.name : "Document"}</h4>
                      ) : (
                        <h4>
                          {doc?.file2?.name ? doc?.file2?.name : "Document"}
                        </h4>
                      )}
                    </>

                    {doc?.file ? (
                      <button
                        className="dellbutttons"
                        onClick={() => {
                          setProjectSetup((prev) => ({
                            ...prev,
                            documents: {
                              ...prev.documents,
                              value: prev.documents.value.map((d, index) =>
                                index === i
                                  ? { ...d, file: null, file2: null }
                                  : d
                              ),
                            },
                          }));
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        Delete
                      </button>
                    ) : (
                      <button
                        onClick={() => uploadDoc(i)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            d="M10 4L10 13"
                            stroke="#02171D"
                            strokeWidth="1.2"
                            strokeLinecap="square"
                          />
                          <path
                            d="M14.1667 6.66667L10 2.5L5.83337 6.66667"
                            stroke="#02171D"
                            strokeWidth="1.2"
                            strokeLinecap="square"
                          />
                          <path
                            d="M17.5 12.5V15.8333C17.5 16.2754 17.3244 16.6993 17.0118 17.0118C16.6993 17.3244 16.2754 17.5 15.8333 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V12.5"
                            stroke="#02171D"
                            strokeWidth="1.2"
                            strokeLinecap="square"
                          />
                        </svg>
                        Upload
                      </button>
                    )}

                    <input
                      type="file"
                      id={`docInput-${i}`}
                      accept="application/pdf"
                      onChange={(e) => handleDocUpload(i, e, `docInput-${i}`)}
                      hidden
                    />
                  </div>

                  {i > 0 && (
                    <div
                      className="crossssvgs"
                      onClick={() => removeDocumentField(i)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="30"
                        height="30"
                        viewBox="0 0 30 30"
                        fill="none"
                      >
                        <path
                          d="M9 21L21 9.00002"
                          stroke="#FC4F52"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M21 21L9 9"
                          stroke="#FC4F52"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span className="removetexttt">Remove</span>
                    </div>
                  )}
                </div>
              ))}

              <button
                onClick={addDocumentField}
                style={{
                  marginTop: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                Add another
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 10H15"
                    stroke="#02171D"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M10 15V5"
                    stroke="#02171D"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
            {projectSetup?.documents?.error && (
              <p className="error_text mt-3">
                {projectSetup?.documents?.errorMsg}
              </p>
            )}
          </div>
        </section>
      </div>

      <Modal
        className="publish-modall"
        show={show}
        onHide={handleClose}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Crop Image</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="publish_modal">
            <div className="croopppy">
              <div className="cropmodal">
                {imageSrc && (
                  <div
                    className="crop-container inercrop"
                    style={{
                      position: "relative",
                      width: "100%",
                      height: "240px",
                      background: "#E7EEF2",
                      borderRadius: "4px",
                      overflow: "hidden",
                    }}
                  >
                    <Cropper
                      image={imageSrc}
                      crop={crop}
                      zoom={zoom}
                      aspect={cropAspect}
                      onCropChange={setCrop}
                      onCropComplete={onCropComplete}
                      onZoomChange={setZoom}
                    />
                  </div>
                )}
              </div>
              <p>Drag to reposition image</p>

              <div className="zoom-slider">
                <h5>Zoom</h5>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  style={{
                    width: "100%",
                    height: "8px",
                    borderRadius: "10px",
                    appearance: "none",
                    background: `linear-gradient(to right, #329879 0%, #329879 ${
                      ((zoom - 1) / 2) * 100
                    }%, #d0d7de ${((zoom - 1) / 2) * 100}%, #d0d7de 100%)`,
                    outline: "none",
                  }}
                />
              </div>
              <div className="publish_buttons">
                <button className="cancel_button" onClick={handleClose}>
                  Cancel
                </button>
                <button className="confirm_button" onClick={handleSave}>
                  Save Cropped Image
                </button>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Media;
